import { Component, input, inject, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgKitAssetUrlService } from '@wtw/angularkit/ngkit-asset-url';

/**
 * Progress spinner component for visual loading feedback.
 * Displays an animated spinner with configurable size for indicating loading states.
 * The component provides a simple, reusable loading indicator that adapts to different
 * screen sizes through its configurable size input. Use this component to indicate
 * asynchronous operations, data loading, or other long-running processes.
 * @example
 * Basic usage with loading state:
 * ```typescript
 * @Component({
 *   template: `
 *     @if (isLoading()) {
 *       <ngkit-progress-spinner [size]="40" />
 *     }
 *   `
 * })
 * export class MyComponent {
 *   readonly isLoading = signal(false);
 * }
 * ```
 * @example
 * Responsive sizing:
 * ```typescript
 * @Component({
 *   template: `
 *     <ngkit-progress-spinner [size]="spinnerSize()" />
 *   `
 * })
 * export class ResponsiveComponent {
 *   readonly spinnerSize = computed(() => window.innerWidth > 768 ? 50 : 30);
 * }
 * ```
 */
@Component({
  selector: 'ngkit-progress-spinner',
  templateUrl: './ngkit-progress-spinner.component.html',
  styleUrl: './ngkit-progress-spinner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class NgKitProgressSpinnerComponent {
  /** Size of the progress spinner in pixels. Must be a positive integer. Define custom sizes based on layout requirements. */
  readonly size = input.required<number>();

  private readonly assetUrl = inject(NgKitAssetUrlService);

  /** Resolved versioned URL for the spinner SVG asset. */
  protected readonly spinnerUrl = computed(() => this.assetUrl.resolveNgKitAsset('icons/utility/spinner.svg'));
}
